/**
 * Reading the audit trail (ADR-011): what an action and a record are called,
 * and where a record opens.
 *
 * The trail stores codes — `order.status_change`, `price_list` — which are
 * identifiers, not words. Each is worded in the message catalogue under a key
 * built from the code, so a new one is a line of copy rather than a change to
 * the page; a code nobody has worded yet still reads, humanized, as a last
 * resort.
 */

/**
 * The kinds of record the API writes to the trail (`@Audit({ subjectKind })`).
 * A code list for the filter, like order statuses: the API has no endpoint
 * that lists them.
 */
export const AUDIT_SUBJECT_KINDS = [
  'order',
  'invoice',
  'return',
  'product',
  'variant',
  'category',
  'attribute',
  'option',
  'option_value',
  'size_chart',
  'blog_post',
  'giftcard',
  'affiliate',
  'customer_group',
  'follow_up',
  'stock_location',
  'redirect',
  'productField',
  'brand',
  'look',
  'image',
  'video',
  'file',
  'upload_batch',
  'mapping_profile',
  'customer',
  'user',
  'address',
  'cart',
  'trade_application',
  'price_list',
  'discount',
  'popup',
  'automation',
  'subscriber',
  'promotion',
  'campaign',
  'review',
  'question',
  'ticket',
  'message',
  'emailTemplate',
  'setting',
  'shippingZone',
  'shippingMethod',
  'paymentProvider',
  'integration',
  'webhook',
  'apiKey',
  'staffGroup',
  'reportView',
] as const;

/** The message key an action's label lives under: `order.status_change` → `auditAction_order_status_change`. */
export function actionKey(action: string): string {
  return `auditAction_${action.replace(/\./g, '_')}`;
}

/** The message key a record kind's name lives under: `price_list` → `auditKind_price_list`. */
export function kindKey(kind: string): string {
  return `auditKind_${kind}`;
}

/** The message key an actor's role is named by — the team screen's words, and "customer" for a shopper. */
export function roleKey(role: string): string | null {
  if (role === 'OWNER') return 'roleOWNER';
  if (role === 'STAFF') return 'roleSTAFF';
  if (role === 'CUSTOMER') return 'customer';
  return null;
}

/**
 * The last resort for a code nobody has worded yet: `category.productFields.set`
 * → `Category · product fields · set`. Readable, if English, and never blank.
 */
export function humanizeCode(code: string): string {
  const words = code
    .split('.')
    .map((part) =>
      part
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase(),
    )
    .filter(Boolean)
    .join(' · ');

  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * Where a record opens in this admin, or null when it has no page of its own.
 *
 * A `user` is a customer unless a `staff.*` action touched it: staff accounts
 * are edited on the team screen, which has no page per person.
 */
export function subjectHref(kind: string | null, id: string | null, action: string): string | null {
  if (!kind || !id) return null;

  const encoded = encodeURIComponent(id);
  switch (kind) {
    case 'order':
      return `/orders/${encoded}`;
    case 'product':
      return `/products/${encoded}`;
    case 'look':
      return `/looks/${encoded}`;
    case 'customer':
      return `/customers/${encoded}`;
    case 'user':
      return action.startsWith('staff.') ? null : `/customers/${encoded}`;
    default:
      return null;
  }
}
